/**
 * YUMIX — GlowChartDefs: shared recharts <defs> block for the GLOW signature.
 *
 * Standardised in Iter. 386 (Sleep mauve, Hydration blue, Steps orange).
 * Iter. 387 extends it to Activities, Evolution (weight), PowerRegister,
 * RecoveryJournal, MyPlanTab so the visual language stays consistent.
 *
 * Usage inside a recharts <BarChart> or <LineChart>:
 *   <defs>
 *     <GlowChartDefs id="myChart" stops={['#fb923c','#f59e0b','#7c2d12']} />
 *   </defs>
 *   <Bar fill="url(#myChart-gradient)" style={{ filter: GLOW_FILTER('#fb923c') }} />
 *   <Tooltip contentStyle={glowTooltipStyle('#fb923c')} ... />
 *
 * Built-in palettes via the `theme` prop: 'orange' | 'mauve' | 'emerald' |
 * 'rose' | 'cyan' | 'crimson'. Pass `stops` to override.
 */
import React from 'react';

const THEMES = {
  orange:  { stops: ['#fdba74', '#fb923c', '#9a3412'], rgb: '251,146,60' },
  mauve:   { stops: ['#d8b4fe', '#c084fc', '#6b21a8'], rgb: '168,85,247' },
  emerald: { stops: ['#6ee7b7', '#34d399', '#065f46'], rgb: '52,211,153'  },
  rose:    { stops: ['#fda4af', '#fb7185', '#9f1239'], rgb: '244,114,182' },
  cyan:    { stops: ['#67e8f9', '#22d3ee', '#155e75'], rgb: '34,211,238'  },
  crimson: { stops: ['#fca5a5', '#f87171', '#7f1d1d'], rgb: '248,113,113' },
  amber:   { stops: ['#fcd34d', '#f59e0b', '#78350f'], rgb: '245,158,11'  },
};

export const GLOW_FILTER = (rgb) =>
  `drop-shadow(0 0 6px rgba(${rgb},0.65)) drop-shadow(0 0 14px rgba(${rgb},0.30))`;

export const glowTooltipStyle = (rgb) => ({
  backgroundColor: 'rgba(15,3,30,0.95)',
  border: `1px solid rgba(${rgb},0.55)`,
  borderRadius: '10px',
  boxShadow: `0 0 16px rgba(${rgb},0.45), inset 0 0 8px rgba(255,255,255,0.08)`,
  backdropFilter: 'blur(8px)',
  padding: '8px 12px',
});

export const glowTooltipLabelStyle = (rgb) => ({
  color: `rgba(${rgb},1)`,
  fontSize: 11,
  marginBottom: 4,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
});

export const glowTooltipItemStyle = () => ({
  color: '#e9d5ff',
  fontWeight: 600,
});

export const glowCardStyle = (rgb) => ({
  boxShadow: `0 0 0 1px rgba(${rgb},0.30), 0 0 24px rgba(${rgb},0.20)`,
});

const GlowChartDefs = ({ id, theme = 'orange', stops, vertical = false }) => {
  const palette = THEMES[theme] || THEMES.orange;
  const colors = stops || palette.stops;
  return (
    <>
      <linearGradient
        id={`${id}-gradient`}
        x1="0" y1={vertical ? '0' : '0'}
        x2={vertical ? '1' : '0'} y2={vertical ? '0' : '1'}
      >
        <stop offset="0%"   stopColor={colors[0]} stopOpacity={1} />
        <stop offset="35%"  stopColor={colors[1]} stopOpacity={1} />
        <stop offset="100%" stopColor={colors[2]} stopOpacity={1} />
      </linearGradient>
    </>
  );
};

export const themeRgb = (theme) => (THEMES[theme] || THEMES.orange).rgb;

export default GlowChartDefs;
